/**
 * Product orders — the pure half of the Orders screen. No React, no fetch,
 * no browser global at load; packages/api/test/product-order-web.test.js
 * imports this file directly under Node.
 *
 * A REPLY IS CHECKED BEFORE IT IS BELIEVED. apiFetch resolves a 2xx with a
 * body it could not parse as `{}`. A screen that took that for success would
 * clear a pending create it cannot show, or render an order with no id. So
 * every reply is checked field by field — declared fields present (absent is
 * not null), domains, money as a two-place string, dates as calendar days,
 * and the fields agreeing with the status — and anything short is thrown as
 * MalformedReply. The outcome is then UNKNOWN, not failed and not succeeded.
 *
 * A CREATE IS REMEMBERED BEFORE IT IS SENT. The exact payload and its
 * submission token are written to storage under the signed-in user's id
 * before the POST goes out. Whatever happens next — a dropped connection, a
 * reload, a 502 — the same request can be sent again, and the API's replay
 * rule turns a second arrival into the order the first one made.
 *
 * MONEY AND DATES ARE STRINGS. A total is validated and displayed from its
 * string; a date-only value is split on its hyphens. Neither ever meets
 * Number() or the local timezone.
 */

export const PRODUCT_TYPE_LABELS = Object.freeze({ print: "Print Product", promotional: "Promotional Product" });
export const STATUS_LABELS = Object.freeze({
  awaiting_quote: "Awaiting quote",
  ready_for_billing: "Ready for billing",
  invoiced: "Invoiced",
  cancelled: "Cancelled",
});
const PRODUCT_TYPES = Object.keys(PRODUCT_TYPE_LABELS);
const STATUSES = Object.keys(STATUS_LABELS);
const EVENT_ACTIONS = ["created", "edit", "prepare-billing", "revise", "invoiced", "correct-invoice", "cancel"];

export class MalformedReply extends Error {
  constructor(message) { super(message); this.name = "MalformedReply"; }
}

// ── Shapes ──────────────────────────────────────────────────────────────────

const MONEY = /^\d+\.\d{2}$/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isObject = (v) => v !== null && typeof v === "object" && !Array.isArray(v);
const isText = (v) => typeof v === "string" && v.trim().length > 0;
const isInt = (v) => Number.isInteger(v);
const isInstant = (v) => typeof v === "string" && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d{1,3})?Z$/.test(v) && !Number.isNaN(Date.parse(v));
/** A real calendar day, as a string — "2026-02-30" is not one. */
export function isCalendarDate(v) {
  if (typeof v !== "string" || !DATE.test(v) || v < "1000-01-01") return false;
  const d = new Date(`${v}T00:00:00.000Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === v;
}
const isMoney = (v) => typeof v === "string" && MONEY.test(v) && /[1-9]/.test(v);

/** Each public field, with the test a value must pass. `nullable` means null is also allowed. */
const ORDER_FIELDS = [
  ["id", isText], ["clientId", isText], ["client", (v) => isObject(v) && isText(v.id) && isText(v.name)],
  ["productType", (v) => PRODUCT_TYPES.includes(v)], ["description", isText],
  ["quantity", (v) => isInt(v) && v >= 1 && v <= 1000000], ["requestedDeliveryDate", isCalendarDate, "nullable"],
  ["status", (v) => STATUSES.includes(v)],
  ["approvedTotal", isMoney, "nullable"], ["approvedByName", isText, "nullable"], ["approvalDate", isCalendarDate, "nullable"],
  ["approvalReference", isText, "nullable"], ["approvalRecordedById", isText, "nullable"], ["approvalRecordedAt", isInstant, "nullable"],
  ["qbInvoiceNumber", isText, "nullable"], ["invoiceDate", isCalendarDate, "nullable"], ["invoiceRecordedById", isText, "nullable"], ["invoiceRecordedAt", isInstant, "nullable"],
  ["cancellationReason", isText, "nullable"],
  ["createdById", isText], ["updatedById", isText], ["createdAt", isInstant], ["updatedAt", isInstant],
  ["version", (v) => isInt(v) && v >= 1],
];
const APPROVAL_FIELDS = ["approvedTotal", "approvedByName", "approvalDate", "approvalReference", "approvalRecordedById", "approvalRecordedAt"];
const INVOICE_FIELDS = ["qbInvoiceNumber", "invoiceDate", "invoiceRecordedById", "invoiceRecordedAt"];

function checkOrder(order, where) {
  if (!isObject(order)) throw new MalformedReply(`${where}: no order in the reply`);
  for (const [field, ok, nullable] of ORDER_FIELDS) {
    if (!Object.hasOwn(order, field)) throw new MalformedReply(`${where}: "${field}" is missing`);
    const value = order[field];
    if (value === null && nullable) continue;
    if (value === undefined || !ok(value)) throw new MalformedReply(`${where}: "${field}" is not valid`);
  }
  // The fields must agree with the status, as the database guarantees they do.
  const approvals = APPROVAL_FIELDS.filter((f) => order[f] !== null).length;
  const invoices = INVOICE_FIELDS.filter((f) => order[f] !== null).length;
  const { status } = order;
  if (approvals !== 0 && approvals !== APPROVAL_FIELDS.length) throw new MalformedReply(`${where}: a partial approval`);
  if (status === "awaiting_quote" && approvals !== 0) throw new MalformedReply(`${where}: awaiting a quote yet carrying an approval`);
  if ((status === "ready_for_billing" || status === "invoiced") && approvals === 0) throw new MalformedReply(`${where}: ${status} with no approval`);
  if (status === "invoiced" ? invoices !== INVOICE_FIELDS.length : invoices !== 0) throw new MalformedReply(`${where}: invoice fields disagree with the status`);
  if ((status === "cancelled") !== (order.cancellationReason !== null)) throw new MalformedReply(`${where}: cancellation reason disagrees with the status`);
  return order;
}

function checkEvents(order, where) {
  const { events } = order;
  if (!Array.isArray(events)) throw new MalformedReply(`${where}: no history in the reply`);
  if (events.length !== order.version) throw new MalformedReply(`${where}: ${events.length} event(s) for version ${order.version}`);
  events.forEach((e, i) => {
    const at = `${where}: event ${i + 1}`;
    if (!isObject(e)) throw new MalformedReply(`${at} is not an object`);
    for (const field of ["id", "action", "version", "actorId", "reason", "before", "after", "createdAt"]) {
      if (!Object.hasOwn(e, field)) throw new MalformedReply(`${at}: "${field}" is missing`);
    }
    if (!isText(e.id) || !isText(e.actorId) || !isInstant(e.createdAt)) throw new MalformedReply(`${at}: identity or time is not valid`);
    if (!EVENT_ACTIONS.includes(e.action)) throw new MalformedReply(`${at}: unknown action`);
    if (e.version !== i + 1) throw new MalformedReply(`${at}: out of order`);
    if ((i === 0) !== (e.action === "created")) throw new MalformedReply(`${at}: "created" must be first and only first`);
    if (e.reason !== null && !isText(e.reason)) throw new MalformedReply(`${at}: reason is not valid`);
    if (i === 0 ? e.before !== null : !isObject(e.before)) throw new MalformedReply(`${at}: "before" is not valid`);
    if (!isObject(e.after)) throw new MalformedReply(`${at}: "after" is not valid`);
  });
}

/** `{order}` → the order, or MalformedReply. */
export function requireOrderReply(body) {
  if (!isObject(body)) throw new MalformedReply("order reply: not an object");
  return checkOrder(body.order, "order reply");
}

/** `{order, replayed}` → both, or MalformedReply. */
export function requireCreateReply(body) {
  if (!isObject(body)) throw new MalformedReply("create reply: not an object");
  const order = checkOrder(body.order, "create reply");
  if (typeof body.replayed !== "boolean") throw new MalformedReply('create reply: "replayed" is missing');
  return { order, replayed: body.replayed };
}

/** `{order: {..., events}}` → the order with its history, or MalformedReply. */
export function requireDetailReply(body) {
  if (!isObject(body)) throw new MalformedReply("detail reply: not an object");
  const order = checkOrder(body.order, "detail reply");
  checkEvents(order, "detail reply");
  return order;
}

/** `{items, total, page, pageSize, summary}` → the same, or MalformedReply. */
export function requireListReply(body) {
  if (!isObject(body)) throw new MalformedReply("list reply: not an object");
  for (const field of ["items", "total", "page", "pageSize", "summary"]) {
    if (!Object.hasOwn(body, field)) throw new MalformedReply(`list reply: "${field}" is missing`);
  }
  if (!Array.isArray(body.items)) throw new MalformedReply("list reply: items is not an array");
  if (!isInt(body.total) || body.total < 0 || !isInt(body.page) || body.page < 1 || !isInt(body.pageSize) || body.pageSize < 1) {
    throw new MalformedReply("list reply: total, page or pageSize is not valid");
  }
  if (body.items.length > body.pageSize || body.items.length > body.total) throw new MalformedReply("list reply: more rows than the page or the total allows");
  const { summary } = body;
  if (!isObject(summary) || !isInt(summary.readyCount) || summary.readyCount < 0 || typeof summary.readyTotal !== "string" || !MONEY.test(summary.readyTotal)) {
    throw new MalformedReply("list reply: summary is not valid");
  }
  body.items.forEach((row, i) => checkOrder(row, `list reply: row ${i + 1}`));
  return body;
}

// ── The pending create ──────────────────────────────────────────────────────

const PENDING_VERSION = 1;
const PAYLOAD_KEYS = ["submissionToken", "clientId", "productType", "description", "quantity", "requestedDeliveryDate"];

function isActor(actorId) { return typeof actorId === "string" && actorId.trim().length > 0; }
function isPayload(p) {
  return isObject(p) && Object.keys(p).length === PAYLOAD_KEYS.length && PAYLOAD_KEYS.every((k) => Object.hasOwn(p, k))
    && typeof p.submissionToken === "string" && UUID.test(p.submissionToken) && isText(p.clientId) && PRODUCT_TYPES.includes(p.productType)
    && isText(p.description) && isInt(p.quantity) && p.quantity >= 1 && p.quantity <= 1000000
    && (p.requestedDeliveryDate === null || isCalendarDate(p.requestedDeliveryDate));
}
const copyPayload = (p) => Object.fromEntries(PAYLOAD_KEYS.map((k) => [k, p[k]]));

/** The storage key: one pending create per signed-in user. */
export function pendingCreateKey(actorId) {
  if (!isActor(actorId)) throw new TypeError("a pending create needs a signed-in user");
  return `prism:product-order:pending-create:${actorId}`;
}

/**
 * Remember the exact request about to be sent. Returns false — never throws
 * — when storage is unavailable, so the caller can say so and keep the
 * request in memory instead.
 */
export function savePendingCreate(storage, actorId, payload) {
  const key = pendingCreateKey(actorId);
  // The payload is the request and nothing else: a token, a session or a
  // price mixed in would be refused, not quietly written to storage.
  if (!isPayload(payload)) throw new TypeError("not a product-order create payload");
  try {
    storage.setItem(key, JSON.stringify({ v: PENDING_VERSION, actorId, payload: copyPayload(payload) }));
    return true;
  } catch {
    return false;
  }
}

/** The request remembered for this user, as a fresh copy — or null. */
export function readPendingCreate(storage, actorId) {
  const key = pendingCreateKey(actorId);
  let raw;
  try { raw = storage.getItem(key); } catch { return null; }
  if (typeof raw !== "string" || raw === "") return null;
  let stored;
  try { stored = JSON.parse(raw); } catch { stored = null; }
  if (!isObject(stored) || stored.v !== PENDING_VERSION || stored.actorId !== actorId || !isPayload(stored.payload)) {
    // Whatever this is, it is not a request this user can resend.
    try { storage.removeItem(key); } catch { /* nothing to do */ }
    return null;
  }
  return copyPayload(stored.payload);
}

export function clearPendingCreate(storage, actorId) {
  const key = pendingCreateKey(actorId);
  try { storage.removeItem(key); return true; } catch { return false; }
}

// ── Building requests from a form ───────────────────────────────────────────

const text = (v) => (typeof v === "string" ? v.trim() : "");
const LIMITS = { description: 4000, approvedByName: 200, approvalReference: 1000, qbInvoiceNumber: 40, reason: 1000 };

function requestFields(form, errors) {
  const productType = text(form?.productType);
  const description = text(form?.description);
  const quantityText = text(form?.quantity);
  const requestedDeliveryDate = text(form?.requestedDeliveryDate);
  if (!PRODUCT_TYPES.includes(productType)) errors.productType = "Choose Print Product or Promotional Product";
  if (description.length < 1) errors.description = "Describe what the client asked for";
  else if (description.length > LIMITS.description) errors.description = `At most ${LIMITS.description.toLocaleString()} characters`;
  // Converted exactly once, from digits only: "1.5", "1e3" and "1,000" are
  // not quantities, and a leading zero is not an integer this form accepts.
  const quantity = /^[1-9]\d{0,6}$/.test(quantityText) ? Number(quantityText) : null;
  if (quantity === null || quantity > 1000000) errors.quantity = "A whole number from 1 to 1,000,000";
  if (requestedDeliveryDate !== "" && !isCalendarDate(requestedDeliveryDate)) errors.requestedDeliveryDate = "Enter a real date, or leave it blank";
  return { productType, description, quantity, requestedDeliveryDate: requestedDeliveryDate === "" ? null : requestedDeliveryDate };
}

/** The create payload for a form, or `{payload: null, errors}`. The token is minted by the caller, once. */
export function buildCreatePayload(form, submissionToken) {
  if (typeof submissionToken !== "string" || !UUID.test(submissionToken)) throw new TypeError("a submission token is required");
  const errors = {};
  const clientId = text(form?.clientId);
  if (clientId.length < 1) errors.clientId = "Choose a client";
  const fields = requestFields(form, errors);
  if (Object.keys(errors).length) return { payload: null, errors };
  return { payload: { submissionToken: submissionToken.toLowerCase(), clientId, ...fields }, errors };
}

function requireVersion(version) {
  if (!isInt(version) || version < 1) throw new TypeError("the displayed version is required");
  return version;
}

/** The edit body: the request fields and the version on screen. No client — it is immutable. */
export function buildEditInput(form, version) {
  requireVersion(version);
  const errors = {};
  const fields = requestFields(form, errors);
  return Object.keys(errors).length ? { input: null, errors } : { input: { version, ...fields }, errors };
}

/** The approval body. The total stays the string that was typed. */
export function buildApprovalInput(form, version, today) {
  requireVersion(version);
  const errors = {};
  const approvedTotal = text(form?.approvedTotal);
  const approvedByName = text(form?.approvedByName);
  const approvalDate = text(form?.approvalDate);
  const approvalReference = text(form?.approvalReference);
  if (!/^(0|[1-9]\d{0,9})(\.\d{1,2})?$/.test(approvedTotal) || !/[1-9]/.test(approvedTotal)) errors.approvedTotal = "An amount above zero, with at most two decimals (for example 1250.00)";
  if (approvedByName.length < 1) errors.approvedByName = "Who at the client approved it";
  else if (approvedByName.length > LIMITS.approvedByName) errors.approvedByName = `At most ${LIMITS.approvedByName} characters`;
  if (!isCalendarDate(approvalDate)) errors.approvalDate = "Enter the date the client approved";
  else if (approvalDate > today) errors.approvalDate = "The approval date cannot be in the future";
  if (approvalReference.length < 1) errors.approvalReference = "Where the approval is recorded (email subject and date, call note…)";
  else if (approvalReference.length > LIMITS.approvalReference) errors.approvalReference = `At most ${LIMITS.approvalReference.toLocaleString()} characters`;
  return Object.keys(errors).length ? { input: null, errors } : { input: { version, approvedTotal, approvedByName, approvalDate, approvalReference }, errors };
}

/** The invoice body: the QuickBooks number and the date it was created there. */
export function buildInvoiceInput(form, version, today) {
  requireVersion(version);
  const errors = {};
  const qbInvoiceNumber = text(form?.qbInvoiceNumber);
  const invoiceDate = text(form?.invoiceDate);
  if (qbInvoiceNumber.length < 1) errors.qbInvoiceNumber = "The QuickBooks invoice number";
  else if (qbInvoiceNumber.length > LIMITS.qbInvoiceNumber) errors.qbInvoiceNumber = `At most ${LIMITS.qbInvoiceNumber} characters`;
  if (!isCalendarDate(invoiceDate)) errors.invoiceDate = "Enter the invoice date";
  else if (invoiceDate > today) errors.invoiceDate = "The invoice date cannot be in the future";
  return Object.keys(errors).length ? { input: null, errors } : { input: { version, qbInvoiceNumber, invoiceDate }, errors };
}

/** The body for revise, correct-invoice and cancel: a reason is required. */
export function buildReasonInput(form, version) {
  requireVersion(version);
  const reason = text(form?.reason);
  if (reason.length < 1) return { input: null, errors: { reason: "A reason is required" } };
  if (reason.length > LIMITS.reason) return { input: null, errors: { reason: `At most ${LIMITS.reason.toLocaleString()} characters` } };
  return { input: { version, reason }, errors: {} };
}

// ── The list query ──────────────────────────────────────────────────────────

/**
 * Exactly the four filters the API accepts, from an explicit allowlist. The
 * page's own URL may carry view-as and date-range keys; none is forwarded,
 * and a value the API would refuse is left out rather than sent.
 */
export function listQueryString(filters) {
  const params = new URLSearchParams();
  const status = filters?.status;
  if (typeof status === "string" && status !== "open" && ["all", ...STATUSES].includes(status)) params.set("status", status);
  const clientId = text(filters?.clientId);
  if (clientId.length >= 1 && clientId.length <= 200) params.set("clientId", clientId);
  const search = text(filters?.search);
  if (search.length >= 1 && search.length <= 200) params.set("search", search);
  const page = filters?.page;
  if (isInt(page) && page >= 2 && page <= 100000) params.set("page", String(page));
  return params.toString();
}

const OPEN_STATUSES = ["awaiting_quote", "ready_for_billing"];

/**
 * requireListReply, and then: is this reply an ANSWER TO THIS QUERY? `query`
 * is the string listQueryString built and the request was sent with. Only
 * what the service guarantees for it is held against the reply:
 *   page      the reply's page is the page asked for (absent means 1)
 *   clientId  with a client filter, every row is that client's
 *   status    a named status is every row's status; the default view (no
 *             status key) is open — awaiting a quote or ready for billing;
 *             "all" is anything
 *   ready     under status=ready_for_billing the list and the ready summary
 *             are the same set, counted in one consistent read: total must
 *             equal summary.readyCount
 * The search text is not re-derived here: the database matches it without
 * regard to case, by rules a browser cannot reproduce exactly.
 * A reply that fails any of these is MalformedReply — an error on screen,
 * never another view's rows under this view's controls.
 */
export function requireListReplyFor(body, query) {
  const list = requireListReply(body);
  const asked = new URLSearchParams(typeof query === "string" ? query : "");
  const page = asked.has("page") ? Number(asked.get("page")) : 1;
  if (list.page !== page) throw new MalformedReply(`list reply: page ${list.page} is not the page asked for (${page})`);
  const clientId = asked.get("clientId");
  const status = asked.get("status");
  list.items.forEach((order, i) => {
    if (clientId !== null && order.clientId !== clientId) throw new MalformedReply(`list reply: row ${i + 1} is another client's`);
    if (status === null ? !OPEN_STATUSES.includes(order.status) : status !== "all" && order.status !== status) {
      throw new MalformedReply(`list reply: row ${i + 1} is ${order.status}, which is not what was asked for`);
    }
  });
  if (status === "ready_for_billing" && list.total !== list.summary.readyCount) {
    throw new MalformedReply(`list reply: ${list.total} ready order(s) listed but ${list.summary.readyCount} counted`);
  }
  return list;
}

// ── Display ─────────────────────────────────────────────────────────────────

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-10-20" → "Oct 20, 2026", from the string alone; null → "Not specified". */
export function formatDateOnly(ymd) {
  if (ymd === null || ymd === undefined || ymd === "") return "Not specified";
  if (!isCalendarDate(ymd)) throw new TypeError(`not a calendar date: ${String(ymd)}`);
  const [y, m, d] = ymd.split("-");
  return `${MONTHS[Number(m) - 1]} ${Number(d)}, ${y}`;
}

/** "1234567.50" → "$1,234,567.50", by string grouping; null → "Quote needed". */
export function formatTotal(total) {
  if (total === null) return "Quote needed";
  if (typeof total !== "string" || !MONEY.test(total)) throw new TypeError(`not a money string: ${String(total)}`);
  const [whole, cents] = total.split(".");
  return `$${whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}.${cents}`;
}

/** The text handed to QuickBooks: what was ordered, how many, by when. Never a price. */
export function billingDescription(order) {
  return `${PRODUCT_TYPE_LABELS[order.productType]} — ${order.description}\nQuantity: ${order.quantity}\nRequested delivery: ${order.requestedDeliveryDate ?? "Not specified"}`;
}

/** The UTC calendar day — what the server compares an approval or invoice date against. */
export function utcToday(now = new Date()) {
  return now.toISOString().slice(0, 10);
}

// ── What a failure means ────────────────────────────────────────────────────

/**
 * For a create:
 *   rejected      400, 403, 404 — the server refused before writing; a
 *                 corrected request under a NEW token is safe
 *   conflict      409 — the token is known with a different payload or actor;
 *                 something was written once; refresh and review
 *   unauthorized  401 — sign in again; the pending request is kept
 *   unknown       anything else: no reply, a 5xx, a reply that could not be
 *                 read. The server may have committed. Only the SAME request
 *                 may be sent again.
 */
export function classifyFailure(err) {
  const status = err?.status;
  if (status === 400 || status === 403 || status === 404) return "rejected";
  if (status === 409) return "conflict";
  if (status === 401) return "unauthorized";
  return "unknown";
}

/** Actions the screen offers for an order and a role. The API decides; this only avoids offering what it will refuse. */
export function availableActions(order, role) {
  const admin = role === "SUPER_ADMIN" || role === "ADMIN";
  const accountant = role === "ACCOUNTANT";
  switch (order?.status) {
    case "awaiting_quote": return admin ? ["edit", "prepare-billing", "cancel"] : [];
    case "ready_for_billing": return admin ? ["invoiced", "revise", "cancel"] : accountant ? ["invoiced"] : [];
    case "invoiced": return admin ? ["correct-invoice"] : [];
    default: return [];
  }
}

// ── Acquiring storage ───────────────────────────────────────────────────────

/**
 * `window.sessionStorage`, or null. The GETTER itself can throw (a
 * SecurityError when storage is blocked), and storage that exists can still
 * refuse to write. Both yield null, and the screen then says plainly that a
 * reload will not recover a pending request — while keeping it in memory.
 */
export function acquireSessionStorage(win) {
  let storage;
  try { storage = win?.sessionStorage; } catch { return null; }
  if (!storage || typeof storage.getItem !== "function" || typeof storage.setItem !== "function" || typeof storage.removeItem !== "function") return null;
  const probe = "prism:product-order:storage-probe";
  try {
    storage.setItem(probe, "1");
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

// ── The detail's state, bound to one order, one visit, one request ──────────
//
// Every event carries the id of the order it is about and the GENERATION of
// the selection it belongs to; loads also carry a REQUEST number. The reducer
// ignores anything that is not about the current order, the current visit to
// it (A→B→A is three generations, and the first visit's late success,
// failure or mutation settlement must not touch the third) or, for loads, the
// latest request (two overlapping reloads: only the newest may update the
// detail, whichever lands first).
//
// ONE WAY TO UNLOCK, ONE WAY TO RE-ENABLE. After a mutation whose outcome is
// unknown the detail is `locked`; after a saved or conflicting one it is
// `stale` — what is shown is behind the server. Both block every action, and
// both are cleared by exactly one event: `loaded` for this order, this
// generation, from the latest request, with a detail that names this order.
// Not a saved mutation, not a load for another order, not an obsolete reply,
// not a retry button of its own.

const RELOAD_FAILED = " The order could not be reloaded, so actions are paused until a reload succeeds.";

/** The state for a freshly selected order: nothing shown, nothing actionable. `gen` 0 is "nothing selected yet". */
export function detailInitial(id, gen = 0) {
  return { id, gen, request: 0, order: null, loading: true, error: null, locked: false, stale: false, busy: false, action: null, notice: null, fieldErrors: {} };
}

const thisVisit = (state, event) => event.id === state.id && event.gen === state.gen;
const latestRequest = (state, event) => thisVisit(state, event) && event.request === state.request;

export function detailReduce(state, event) {
  switch (event.type) {
    case "select":
      return event.gen > state.gen ? detailInitial(event.id, event.gen) : state;
    case "loadStarted":
      if (!thisVisit(state, event) || !(event.request > state.request)) return state;
      return { ...state, request: event.request, loading: true };
    case "loaded": {
      if (!latestRequest(state, event)) return state;
      if (!event.order || event.order.id !== state.id) {
        return { ...state, loading: false, error: new MalformedReply(`detail reply is for ${event.order?.id ?? "no order"}, not ${state.id}`) };
      }
      // Unlocking: the pause is over, but an earlier uncertainty is still
      // worth a word — what is shown is the server's state, to be checked.
      // A saved or conflict notice stays as it was, minus the reload failure.
      const notice = state.locked
        ? { tone: "warning", text: "A change could not be confirmed earlier. This is the order's current state — check it before doing anything further." }
        : state.notice ? { ...state.notice, text: state.notice.text.replace(RELOAD_FAILED, "") } : null;
      return { ...state, order: event.order, loading: false, error: null, locked: false, stale: false, notice };
    }
    case "loadFailed": {
      if (!latestRequest(state, event)) return state;
      const blocked = state.locked || state.stale;
      const base = state.notice?.text?.replace(RELOAD_FAILED, "") ?? "A change could not be confirmed.";
      const notice = blocked ? { tone: "warning", text: base + RELOAD_FAILED } : state.notice;
      return { ...state, loading: false, error: event.error, notice };
    }
    case "openAction":
      return canActOnDetail(state) ? { ...state, action: event.action, fieldErrors: {}, notice: null } : state;
    case "closeAction":
      return state.busy ? state : { ...state, action: null, fieldErrors: {} };
    case "validationFailed":
      // The form itself was wrong; nothing was sent. The errors are shown
      // beside the fields and the dialog stays open on what was typed.
      if (!thisVisit(state, event)) return state;
      return { ...state, fieldErrors: event.fieldErrors ?? {}, notice: { tone: "error", text: event.message || "Check the highlighted fields." } };
    case "mutationStarted":
      if (!thisVisit(state, event) || !canActOnDetail(state)) return state;
      return { ...state, busy: true, notice: null, fieldErrors: {} };
    case "mutationSettled": {
      if (!thisVisit(state, event) || !state.busy) return state;
      const base = { ...state, busy: false };
      switch (event.outcome) {
        case "saved":
          return { ...base, action: null, stale: true, notice: { tone: "info", text: `${event.label}: saved.` } };
        case "rejected":
          return { ...base, fieldErrors: event.details && typeof event.details === "object" ? event.details : {}, notice: { tone: "error", text: event.message || "The request was refused." } };
        case "conflict":
          return { ...base, action: null, stale: true, notice: { tone: "warning", text: "This order changed. Review the latest details before trying again." } };
        default:
          return { ...base, action: null, locked: true, notice: { tone: "warning", text: `We couldn't confirm whether "${event.label}" was saved. This is the order's current state — check it before doing anything further.` } };
      }
    }
    default:
      return state;
  }
}

/** May an action be offered right now? Only on a loaded, current, fresh, unlocked, idle detail. */
export function canActOnDetail(state) {
  return state.order !== null && state.order.id === state.id && !state.busy && !state.locked && !state.stale && !state.loading && state.error === null;
}

// ── What the detail component does, with the API injected ───────────────────
//
// This is the code ProductOrderDetail runs; the component only wires it to
// React (useReducer for the state, a ref for getState) and renders. Keeping
// it here means the real sequence — select, the loads it issues, a mutation,
// the refresh after it, a retry — can be driven in a test with promises that
// are settled by hand, late and out of order.

export const DETAIL_ACTION_LABELS = {
  edit: "Edit request", "prepare-billing": "Record approval & prepare billing", invoiced: "Record QuickBooks invoice",
  revise: "Return for revised quote", "correct-invoice": "Correct invoice recording", cancel: "Cancel order",
};

/**
 * @param api        (path, init?) => Promise<body>; rejects with an Error carrying `status` on HTTP failure
 * @param dispatch   feeds detailReduce
 * @param getState   the current reduced state
 * @param onChanged  told after any outcome other than a rejection, so the list can refresh
 */
export function createDetailOps({ api, dispatch, getState, onChanged }) {
  let gen = 0;
  let request = 0;
  let id = null;
  // The visit whose mutation is in flight, if any. The reducer refuses a
  // second mutationStarted while busy, but a React ref only sees the reduced
  // state after a render — so ops refuses the second request itself.
  let mutating = null;

  /** The one way a detail reaches the screen: fetch, validate, tag, dispatch. The reducer judges the tag. */
  async function refresh() {
    const tag = { id, gen, request: ++request };
    dispatch({ type: "loadStarted", ...tag });
    try {
      const body = await api(`/api/product-orders/${encodeURIComponent(tag.id)}`);
      dispatch({ type: "loaded", ...tag, order: requireDetailReply(body) });
    } catch (e) {
      if (e?.status === 401) return; // the session is over; the caller's api has logged out
      dispatch({ type: "loadFailed", ...tag, error: e instanceof Error ? e : new Error("The order could not be loaded") });
    }
  }

  /** A new visit: a new generation, request numbering restarted, and the first load. */
  function select(nextId) {
    id = nextId;
    gen += 1;
    request = 0;
    dispatch({ type: "select", id, gen });
    return refresh();
  }

  async function change(kind, input) {
    const state = getState();
    if (!canActOnDetail(state) || mutating === state.gen) return;
    const tag = { id: state.id, gen: state.gen };
    const label = DETAIL_ACTION_LABELS[kind];
    mutating = tag.gen;
    dispatch({ type: "mutationStarted", ...tag });
    const path = kind === "edit" ? `/api/product-orders/${encodeURIComponent(tag.id)}` : `/api/product-orders/${encodeURIComponent(tag.id)}/${kind}`;
    let outcome;
    try {
      const body = await api(path, { method: kind === "edit" ? "PUT" : "POST", body: JSON.stringify(input) });
      const saved = requireOrderReply(body);
      if (saved.id !== tag.id) throw new MalformedReply(`the reply is about ${saved.id}, not ${tag.id}`);
      outcome = { outcome: "saved" };
    } catch (e) {
      const kindOf = classifyFailure(e);
      if (kindOf === "unauthorized") { mutating = null; return; }
      outcome = kindOf === "rejected" ? { outcome: "rejected", message: e.message, details: e.details } : { outcome: kindOf === "conflict" ? "conflict" : "unknown" };
    }
    // Settled — for the visit it was sent from. If the user has moved on the
    // reducer ignores this, and no refresh is issued for the stale mutation.
    if (mutating === tag.gen) mutating = null;
    dispatch({ type: "mutationSettled", ...tag, label, ...outcome });
    if (outcome.outcome === "rejected") return;
    onChanged?.();
    if (tag.gen === gen && tag.id === id) await refresh();
  }

  /** The edit form was submitted: validate locally first, send only what passes. */
  function submitEdit(form) {
    const state = getState();
    if (!canActOnDetail(state)) return;
    const built = buildEditInput(form, state.order.version);
    if (built.input) return change("edit", built.input);
    dispatch({ type: "validationFailed", id: state.id, gen: state.gen, fieldErrors: built.errors, message: "Check the highlighted fields." });
  }

  const openAction = (action) => dispatch({ type: "openAction", action });
  const closeAction = () => dispatch({ type: "closeAction" });

  return { select, refresh, change, submitEdit, openAction, closeAction };
}

// ── The list's state, bound to one query and one request ────────────────────
//
// The list is asked a QUERY — status, client, search, page, as the string
// listQueryString builds — and what it shows is rows, a ready total and a
// page position that are only true of that query. So a result is kept with
// the query it answers, every load carries a REQUEST number that only ever
// goes up, and:
//
//   - a reply is accepted only for the current query AND the latest request.
//     A→B→A asks the same string twice; the first reply, landing late, has an
//     older request number and is refused like any other stale reply;
//   - starting a load for a different query drops the previous result at
//     once. Only a silent reload of the SAME query (a write elsewhere on the
//     page) keeps its rows while it runs, and they are shown as updating;
//   - a failed load leaves no rows behind: the error stands alone;
//   - listView is the last word: whatever the state holds, it is shown only
//     under the query the controls show now. React renders with a new URL
//     before the effect that starts the load has run — in that frame the
//     state still holds the old query, complete, and nothing of it is shown.

/** The state for a list that has been asked nothing yet: no rows, waiting. */
export function listInitial(query) {
  return { query, request: 0, result: null, loading: true, error: null };
}

const thisListLoad = (state, event) => event.request === state.request && event.query === state.query;

export function listReduce(state, event) {
  switch (event.type) {
    case "loadStarted": {
      if (!(event.request > state.request)) return state;
      const keep = event.silent === true && event.query === state.query && state.error === null;
      return { query: event.query, request: event.request, result: keep ? state.result : null, loading: true, error: null };
    }
    case "loaded":
      if (!thisListLoad(state, event)) return state;
      return { ...state, result: event.result, loading: false, error: null };
    case "loadFailed":
      if (!thisListLoad(state, event)) return state;
      return { ...state, result: null, loading: false, error: event.error };
    default:
      return state;
  }
}

/**
 * What may be rendered under controls that show `query`:
 *   { phase: "loading" }                  nothing for this query yet
 *   { phase: "error", error }             this query's load failed
 *   { phase: "ready", list, refreshing }  this query's result; `refreshing`
 *                                         while a silent reload of it runs
 */
export function listView(state, query) {
  if (state.query !== query) return { phase: "loading" };
  if (state.error) return { phase: "error", error: state.error };
  if (state.result === null) return { phase: "loading" };
  return { phase: "ready", list: state.result, refreshing: state.loading };
}

/**
 * What ProductOrderList does, with the API injected — the list's counterpart
 * of createDetailOps, so the real sequence can be driven in a test with
 * replies settled by hand, late and out of order.
 *
 * @param api       (path) => Promise<body>; rejects with an Error carrying `status` on HTTP failure
 * @param dispatch  feeds listReduce
 */
export function createListOps({ api, dispatch }) {
  // One counter for every load this list ever makes — never restarted for a
  // new query, which is what tells two visits to the same query apart.
  let request = 0;
  // Bumped by dispose: a load from before it may dispatch nothing.
  let mount = 0;
  let current = null; // { query, refreshKey } once sync has been called

  /** The one way a result reaches the screen: fetch, validate against the query, tag, dispatch. The reducer judges the tag. */
  async function load(silent) {
    const tag = { query: current.query, request: ++request };
    const mounted = mount;
    dispatch({ type: "loadStarted", ...tag, silent });
    try {
      const body = await api(`/api/product-orders${tag.query ? `?${tag.query}` : ""}`);
      const result = requireListReplyFor(body, tag.query);
      if (mounted === mount) dispatch({ type: "loaded", ...tag, result });
    } catch (e) {
      if (mounted !== mount) return;
      if (e?.status === 401) return; // the session is over; the caller's api has logged out
      const error = e instanceof MalformedReply ? new MalformedReply(`The order list could not be read: ${e.message}`)
        : e instanceof Error ? e : new Error("The order list could not be loaded");
      dispatch({ type: "loadFailed", ...tag, error });
    }
  }

  /**
   * Called with the query the controls show and the page's refresh key,
   * whenever either may have changed. A different query is a plain load of it
   * — whatever the refresh key did. The same query under a new refresh key is
   * a silent reload. Neither changed: nothing. Never more than one request.
   */
  function sync(query, refreshKey) {
    const fresh = current === null || current.query !== query;
    const refreshed = !fresh && current.refreshKey !== refreshKey;
    current = { query, refreshKey };
    if (fresh) return load(false);
    if (refreshed) return load(true);
    return undefined;
  }

  /** After an error: a plain load of the current query. */
  const retry = () => (current === null ? undefined : load(false));

  /** The list is gone. Nothing in flight may dispatch; a later sync (a re-mount) starts over with a load. */
  function dispose() {
    mount += 1;
    current = null;
  }

  return { sync, retry, dispose };
}

// ── Delma's tab on the billing desk (Task 6) ────────────────────────────────
//
// The billing page hosts two unrelated lists under one URL: the contract
// billing items it always had, and product orders. They must never share
// query state — a product page number must not reach /api/billing/items and
// a contract sort must not reach /api/product-orders. So a tab switch carries
// ONLY the tab and the client, and the product filters live under keys of
// their own (productStatus, productSearch, productPage) that nothing else
// reads.

/** The URL for a billing tab: the tab, the client if one is chosen, nothing else. */
export function billingTabParams(tab, clientId) {
  const params = new URLSearchParams({ tab });
  if (clientId) params.set("clientId", clientId);
  return params;
}

export const PRODUCT_BILLING_STATUSES = ["ready_for_billing", "invoiced", "all"];
const PRODUCT_BILLING_KEYS = { status: "productStatus", clientId: "clientId", search: "productSearch", page: "productPage" };

/** The product tab's list filters, from its own keys. The contract tabs' keys (page, search, status, sort, month) are not read. */
export function readProductBillingFilters(params) {
  const status = params.get(PRODUCT_BILLING_KEYS.status) || "";
  const page = Number.parseInt(params.get(PRODUCT_BILLING_KEYS.page) || "1", 10);
  return {
    status: PRODUCT_BILLING_STATUSES.includes(status) ? status : "ready_for_billing",
    clientId: params.get(PRODUCT_BILLING_KEYS.clientId) || "",
    search: params.get(PRODUCT_BILLING_KEYS.search) || "",
    page: Number.isInteger(page) && page >= 1 ? page : 1,
  };
}

/** Set one product filter on the given params (mutated and returned). Any filter but the page itself drops the page back to 1. */
export function writeProductBillingFilter(params, key, value) {
  const urlKey = PRODUCT_BILLING_KEYS[key];
  if (!urlKey) throw new TypeError(`unknown product billing filter: ${key}`);
  if (value) params.set(urlKey, String(value)); else params.delete(urlKey);
  if (key !== "page") params.delete(PRODUCT_BILLING_KEYS.page);
  return params;
}

/** What the billing desk offers on an order: recording an invoice, and — for admins — returning for a revised quote or correcting a recording. Quoting and cancelling stay on the Orders screen. */
const BILLING_ACTIONS = ["invoiced", "revise", "correct-invoice"];
export function billingActions(order, role) {
  return availableActions(order, role).filter((a) => BILLING_ACTIONS.includes(a));
}

/** Copy to the clipboard; false when there is no clipboard or it refuses, so the caller can fall back to selectable text. */
export async function copyText(text, clipboard) {
  if (!clipboard || typeof clipboard.writeText !== "function") return false;
  try { await clipboard.writeText(text); return true; } catch { return false; }
}
